// CrazyGames SDK v3 seam — ported from WOBBLE LEAGUE (wobble-league/client/src/cg.ts) — same seam, TOTALLY NORMAL TOWN rooms.
// Every call is a no-op until the SDK is loaded AND initialised in a non-"disabled" environment, so one bundle runs
// unchanged on normaltown.whalewars.space, inside the Freeplay arcade and on crazygames.com.
//
// The SDK is loaded only when the page is plausibly inside CrazyGames: served from a crazygames.com host, a crazygames
// referrer / ancestor origin, or `?cg=1` (local testing — on localhost the SDK runs in "local" mode).
// Docs: docs.crazygames.com/sdk/{intro,game,user,data,video-ads}, /requirements/{multiplayer,ads,technical}.

const SDK_SRC = "https://sdk.crazygames.com/crazygames-sdk-v3.js";
const CG_HOST = /(^|\.)crazygames\.com$/i;
const ROOM_PARAM = "room";          // our six-letter table code; unique on the one server (Dallas)
const REGION = "us";                // one region today; in the invite params so a second region can refuse cross-joins

const hostOf = (u: string) => { try { return new URL(u).hostname; } catch { return ""; } };
function wantsSdk(): boolean {
  if (new URLSearchParams(location.search).get("cg") === "1") return true;
  if (CG_HOST.test(location.hostname)) return true;
  if (document.referrer && CG_HOST.test(hostOf(document.referrer))) return true;
  const anc = (location as any).ancestorOrigins as DOMStringList | undefined;
  if (anc) for (let i = 0; i < anc.length; i++) if (CG_HOST.test(hostOf(anc[i]))) return true;
  return false;
}

let live: any | null = null;
const joinListeners: ((code: string) => void)[] = [];

async function boot(): Promise<void> {
  if (!wantsSdk()) return;
  try {
    await new Promise<void>((ok, bad) => { const s = document.createElement("script"); s.src = SDK_SRC; s.async = true; s.onload = () => ok(); s.onerror = () => bad(new Error("sdk load failed")); document.head.appendChild(s); });
    const S = (window as any).CrazyGames?.SDK; if (!S) return;
    await S.init();
    live = S.environment && S.environment !== "disabled" ? S : null;
    if (!live) return;
    try { live.game.addJoinRoomListener((p: any) => { const c = typeof p?.[ROOM_PARAM] === "string" ? p[ROOM_PARAM] : ""; if (c) for (const f of joinListeners) f(c.toUpperCase()); }); } catch {}
    console.log(`[cg] sdk ready · env=${live.environment} · instant=${!!live.game.isInstantMultiplayer}`);
  } catch (e) { live = null; console.warn("[cg] sdk unavailable:", (e as any)?.message || e); }
}
/** resolves once the SDK is up, or we know it is not wanted / failed */
export const ready: Promise<void> = boot();
export const hosted = () => !!live;

/** the table a friend's invite points at (CG invite param), or "" */
export function inviteCode(): string {
  try { const v = live?.game?.getInviteParam?.(ROOM_PARAM); if (typeof v === "string" && /^[A-Za-z0-9]{4,8}$/.test(v)) return v.toUpperCase(); } catch {}
  return "";
}
export const onJoinRoom = (f: (code: string) => void) => { joinListeners.push(f); };

/** persistent data: the CG data module when hosted (follows the CG account; localStorage for guests, merged on login) */
export function storeGet(k: string): string | null { try { if (live?.data?.getItem) return live.data.getItem(k); } catch {} return null; }
export function storeSet(k: string, v: string): boolean { try { if (live?.data?.setItem) { live.data.setItem(k, v); return true; } } catch {} return false; }

/** the CrazyGames username when logged in (6–20 chars), else null */
export async function username(): Promise<string | null> {
  if (!live) return null;
  try { if (live.user?.isUserAccountAvailable === false) return null; const u = await live.user.getUser(); return u && typeof u.username === "string" && u.username ? u.username : null; } catch { return null; }
}
/** the platform mute wins over our own sliders */
export function muteAudio(): boolean { try { return !!live?.game?.settings?.muteAudio; } catch { return false; } }
export function onSettings(f: (s: { muteAudio?: boolean }) => void) { try { live?.game?.addSettingsChangeListener?.(f); } catch {} }

/** a midgame ad at a natural break. Resolves when it finished, errored or was never shown (cooldown, unfilled, basic launch). */
export function midgameAd(onStarted?: () => void): Promise<boolean> {
  return new Promise((res) => {
    if (!live) return res(false);
    let started = false, done = false;
    const end = (v: boolean) => { if (!done) { done = true; res(v); } };
    // never hold the game on a silent SDK: no adStarted within 4 s = no ad (Basic Launch, unfilled, blocked)
    setTimeout(() => { if (!started) end(false); }, 4000);
    try { live.ad.requestAd("midgame", { adStarted: () => { started = true; try { onStarted?.(); } catch {} }, adFinished: () => end(started), adError: () => end(started) }); } catch { end(false); }
  });
}
export function gameplayStart() { try { live?.game.gameplayStart(); } catch {} }
export function gameplayStop() { try { live?.game.gameplayStop(); } catch {} }
export function happytime() { try { live?.game.happytime(); } catch {} }
export function loadingStart() { try { live?.game.loadingStart(); } catch {} }
export function loadingStop() { try { live?.game.loadingStop(); } catch {} }

/** we sit at a table: publish it (room data) and the invite button */
export function roomJoined(code: string) {
  if (!live || !code) return;
  const inviteParams = { [ROOM_PARAM]: code, region: REGION };
  try { live.game.updateRoom?.({ roomId: `${REGION}-${code}`, isJoinable: true, inviteParams }); } catch {}
  try { live.game.showInviteButton(inviteParams); } catch {}
}
export function roomLeft() {
  if (!live) return;
  try { live.game.leftRoom?.(); } catch {}
  try { live.game.hideInviteButton(); } catch {}
}
/** a link into this table — the CG invite link when hosted, else "" (the caller builds its own) */
export function inviteLink(code: string): string {
  if (!live || !code) return "";
  try { const l = live.game.inviteLink({ [ROOM_PARAM]: code, region: REGION }); return typeof l === "string" ? l : ""; } catch { return ""; }
}

/** a rewarded ad. Resolves true only when the ad finished (a reward is earned); never rewards on adError. */
export function rewardedAd(onStarted?: () => void): Promise<boolean> {
  return new Promise((res) => {
    if (!live) return res(false);
    let started = false;
    try { live.ad.requestAd("rewarded", { adStarted: () => { started = true; try { onStarted?.(); } catch {} }, adFinished: () => res(started), adError: () => res(false) }); } catch { res(false); }
  });
}
/** whether a rewarded ad can be offered at all (hosted; the SDK decides fill at request time) */
// Basic Launch disables ads, and a rewarded button that cannot play a video is a named rejection reason
// (docs.crazygames.com/requirements/ads). Off until CrazyGames grants Full Launch; then flip and re-upload.
export const REWARDED_ON = false;
export const canRewarded = () => REWARDED_ON && !!live;

/** login/logout reloads the game on CrazyGames; we only need to flush the save first */
export function onAuthChange(f: () => void) { try { live?.user?.addAuthListener?.(() => f()); } catch {} }
/** the CrazyGames sign-in prompt — only on an explicit player action, never automatically */
export async function promptLogin(): Promise<boolean> { try { await live?.user?.showAuthPrompt?.(); return true; } catch { return false; } }
export const accountAvailable = () => { try { return !!live?.user?.isUserAccountAvailable; } catch { return false; } };

/** the CG weekly leaderboard (invite-only program). Inert until LEADERBOARD_ON is flipped after admission. */
export const LEADERBOARD_ON = false;
export function submitScore(score: number) {
  if (!LEADERBOARD_ON || !live) return;
  try { live.leaderboards?.submitScore?.({ score: Math.floor(score) }); } catch {}
}
/** the platform's "is this an instant-multiplayer launch" (land straight in a public match) */
export const isInstantMultiplayer = () => { try { return !!live?.game?.isInstantMultiplayer; } catch { return false; } };
